import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";

/**
 * Formatta un numero in italiano: "12.345,67"
 * Usato sia da CurrencyDisplay che da formattazioni sparse.
 */
export function formatCurrencyIT(value, withSymbol = true) {
  if (value === null || value === undefined || value === "" || isNaN(Number(value))) return "—";
  const n = Number(value);
  const formatted = new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: "always",
    minimumGroupingDigits: 1,
  }).format(n);
  return withSymbol ? `${formatted} €` : formatted;
}

/**
 * Mostra un importo monetario readonly.
 */
export function CurrencyDisplay({ value, className = "", withSymbol = true }) {
  return <span className={className}>{formatCurrencyIT(value, withSymbol)}</span>;
}

/**
 * Input monetario.
 * - Memorizza internamente il valore "raw string" mentre l'utente digita
 * - Al blur converte e formatta in "X.XXX,XX"
 * - Accetta sia "." sia "," come separatore decimale (il "." del tastierino
 *   numerico viene trattato come decimale)
 * - Espone onChange con il numero (float JS, formato US 12345.67) o null
 */
export default function CurrencyInput({ value, onChange, onBlur: onBlurProp,
                                       placeholder = "0,00",
                                       className = "", suffix = "€",
                                       decimals = 2, ...rest }) {
  const valueToStr = (v) =>
    v === null || v === undefined || v === ""
      ? ""
      : new Intl.NumberFormat("it-IT", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
          useGrouping: "always",
          minimumGroupingDigits: 1,
        }).format(Number(v));
  const [text, setText] = useState(valueToStr(value));
  const [focused, setFocused] = useState(false);

  // Sync from outside when value changes (and not focused)
  useEffect(() => {
    if (!focused) setText(valueToStr(value));
    // eslint-disable-next-line
  }, [value, focused]);

  const parse = (s) => {
    if (!s) return null;
    // strip everything except digits, comma, dot, minus
    let cleaned = String(s).replace(/[^\d.,-]/g, "");
    // count separators: if both . and , exist, the LAST one is the decimal sep
    const lastDot = cleaned.lastIndexOf(".");
    const lastComma = cleaned.lastIndexOf(",");
    let decSep = "";
    if (lastDot === -1 && lastComma === -1) {
      // integer
      const n = Number(cleaned);
      return isNaN(n) ? null : n;
    }
    decSep = lastDot > lastComma ? "." : ",";
    // remove thousands separators (the OTHER one)
    const thouSep = decSep === "." ? "," : ".";
    const parts = cleaned.split(decSep);
    const intPart = parts[0].replace(new RegExp("\\" + thouSep, "g"), "");
    const decPart = parts[1] || "";
    const n = Number(`${intPart}.${decPart}`);
    return isNaN(n) ? null : n;
  };

  const handleChange = (e) => {
    setText(e.target.value);
  };
  const handleBlur = (e) => {
    setFocused(false);
    const n = parse(text);
    if (n === null) {
      setText("");
      onChange?.(null);
    } else {
      setText(valueToStr(n));
      onChange?.(n);
    }
    // Sprint 4I1.B1.1 hotfix — propaga l'onBlur esterno (es. patchField).
    // PRIMA: l'attributo onBlur={handleBlur} in <Input ...> sovrascriveva
    // silenziosamente qualsiasi onBlur passato via {...rest}, impedendo ai
    // consumer (SpeseVeicoloTable) di triggerare la PATCH dedicata.
    onBlurProp?.(e);
  };
  const handleFocus = () => setFocused(true);

  return (
    <div className="relative">
      <Input
        {...rest}
        type="text"
        inputMode="decimal"
        value={text ?? ""}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={`pr-7 text-right ${className}`}
      />
      {suffix && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  );
}
